import { onScopeDispose, ref } from "vue";
import { useEventListener, useResizeObserver } from "@vueuse/core";

const STICKY_TOP_PROPERTY = "--sp-comp-toolbar-sticky-top-position";

// The edge the tracked element occupies once pinned. A sticky element's in-flow position at
// page top includes everything rendered above it (broadcast banners, preview bars), which is
// not where it sits mid-page — its pinned bottom is its own height plus its sticky top inset.
// Fixed elements already report their pinned position through the bounding rect.
function pinnedBottomEdge(element, rect) {
  const style = window.getComputedStyle(element);
  if (style.position === "sticky") {
    const stickyTop = Number.parseFloat(style.top);
    if (!Number.isNaN(stickyTop)) {
      return Math.max(0, rect.height + stickyTop);
    }
  }
  return Math.max(0, rect.bottom);
}

/**
 * Follows the live geometry of the element behind `resolveElement` (a site header that can
 * grow/shrink on scroll) and applies it to the toolbar. Two nested lifetimes:
 *
 * - **Geometry watch** (whole component lifetime): ResizeObserver on the tracked element,
 *   viewport-resize handling, and a recovery MutationObserver while the selector resolves to
 *   nothing. Reports the element's pinned bottom edge through `onPinnedEdge`, from which the
 *   component derives the stick boundary and anchor scroll padding.
 * - **Stick tracking** (only while stuck): a per-scroll-frame write of the element's live
 *   bottom edge to the host's sticky-top custom property.
 *
 * All geometry work is rAF-coalesced. Listeners and observers dispose with the owning scope.
 *
 * @param {object} options
 * @param {HTMLElement} options.hostElement - The custom-element host receiving the sticky-top property.
 * @param {() => boolean} options.isEnabled - Whether dynamic offset tracking applies at all.
 * @param {() => Element | null} options.resolveElement - Resolves the tracked element from the live DOM.
 * @param {(pinnedEdge: number) => void} options.onPinnedEdge - Receives the tracked element's pinned bottom edge.
 * @param {() => void} options.onViewportWidthChange - Called when the viewport width changes (not on height-only resizes).
 */
export function useDynamicOffsetTracking({
  hostElement,
  isEnabled,
  resolveElement,
  onPinnedEdge,
  onViewportWidthChange,
}) {
  const isWatching = ref(false);
  const isTracking = ref(false);
  const observedElement = ref(null);

  let frame = null;
  let lastViewportWidth = null;
  let recoveryObserver = null;

  useResizeObserver(observedElement, schedule);
  useEventListener(() => (isWatching.value ? window : null), "resize", handleViewportResize, { passive: true });
  useEventListener(() => (isTracking.value ? window : null), "scroll", schedule, { passive: true });

  function schedule() {
    frame ??= requestAnimationFrame(apply);
  }

  // Height-only resizes are mobile URL-bar churn during scrolling and must not disturb the
  // caller's running scroll-padding maximum; only a width change reports as a viewport change.
  function handleViewportResize() {
    if (window.innerWidth !== lastViewportWidth) {
      lastViewportWidth = window.innerWidth;
      onViewportWidthChange();
    }
    schedule();
  }

  function apply() {
    frame = null;
    const element = resolveElement();
    observedElement.value = element;
    if (!element) {
      if (isWatching.value) {
        armRecovery();
      }
      // Tracked element removed/replaced while stuck → drop the inline value so the
      // CSS-defined sticky top takes over instead of freezing at a stale offset.
      if (isTracking.value) {
        hostElement.style.removeProperty(STICKY_TOP_PROPERTY);
      }
      return;
    }
    disarmRecovery();

    const rect = element.getBoundingClientRect();

    // The pinned top position follows where the element is right now …
    if (isTracking.value) {
      const value = `${Math.max(0, rect.bottom)}px`;
      if (hostElement.style.getPropertyValue(STICKY_TOP_PROPERTY) !== value) {
        hostElement.style.setProperty(STICKY_TOP_PROPERTY, value);
      }
    }

    // … while the boundary/padding derivations follow where it sits once pinned.
    onPinnedEdge(pinnedBottomEdge(element, rect));
  }

  // While the selector resolves to nothing (e.g. the shell header mid re-render), neither the
  // ResizeObserver nor — unstuck — any listener would notice a replacement element. Watch the
  // DOM for one, and disconnect as soon as it appears.
  function armRecovery() {
    if (recoveryObserver) {
      return;
    }
    recoveryObserver = new MutationObserver(() => {
      if (resolveElement()) {
        disarmRecovery();
        schedule();
      }
    });
    recoveryObserver.observe(document.body, { childList: true, subtree: true });
  }

  function disarmRecovery() {
    recoveryObserver?.disconnect();
    recoveryObserver = null;
  }

  function startGeometryWatch() {
    if (isWatching.value || !isEnabled()) {
      return;
    }
    isWatching.value = true;
    lastViewportWidth = window.innerWidth;

    // Resolves the element, attaches the ResizeObserver, and applies the initial geometry.
    apply();
  }

  function startStickTracking() {
    if (isTracking.value || !isEnabled()) {
      return;
    }
    isTracking.value = true;

    apply();
  }

  function stopStickTracking() {
    // Never tracking (e.g. a plain sticky-on-scroll toolbar without dynamicOffset) → leave the
    // host's custom property untouched so an author-set inline value is preserved.
    if (!isTracking.value) {
      return;
    }
    isTracking.value = false;

    // Fall back to the CSS-defined sticky-top (e.g. --header-height / main-menu-height token).
    hostElement.style.removeProperty(STICKY_TOP_PROPERTY);
  }

  onScopeDispose(() => {
    disarmRecovery();
    if (frame != null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
  });

  return { startGeometryWatch, startStickTracking, stopStickTracking };
}
